import { keepPreviousData, useQuery, useMutation } from "@tanstack/react-query";
import { fetchApi } from "@/lib/api/index.ts";
import { useToast } from "@/components/ui/use-toast";
import { User } from "./user";

export interface OnboardingData {
  id: string;
  teen: Teen;
  shouldSubmit: boolean;
  mentor: Mentor;
}

export interface Mentor {
  email: string;
}

export interface Teen {
  firstName?: string;
  lastName?: string;
  nickname?: string;
  name?: string;
  birthday?: string;
  age?: number;
  state?: string;
  zipcode?: string;
  address?: string;
  sex?: string;
  gender?: string;
  pronouns?: string;
  race?: string;
  phone?: string;

  // SMS and Contact Preferences
  smsOptIn?: boolean;

  // Insurance Information
  insuranceProvider?: string;

  // Parent/Guardian Information
  parentConsent?: string;
  parentName?: string;
  parentPhone?: string;
  parentOptOutReason?: string;

  // Emergency & Medical Contacts
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  pediatricianConsent?: string;
  pediatricianName?: string;
  pediatricianPhone?: string;

  // Rules and Policies
  groundRules?: {
    confidentiality?: boolean;
    crisis?: boolean;
    availability?: boolean;
    communication?: boolean;
  };
  policies?: {
    terms?: boolean;
    privacy?: boolean;
    hipaa?: boolean;
    research?: boolean;
  };

  // Interests and Preferences
  interests?: string[];
  mentorshipGoal?: string; // Goal for working with mentor

  // Mentor Preferences
  mentorPreferences?: {
    helpWith?: string[];
    helpWithExpanded?: string;
    goals?: string;
    methodOfContact?: string[];
    race?: string;
  };

  // Health Check Information
  healthcheck?: {
    phq1?: string;
    phq2?: string;
    gad1?: string;
    gad2?: string;
    harm?: string;
    safety?: string;
    cssrs1?: string;
    cssrs2?: string;
    cssrs3?: string;
    cssrs3Free?: string;
    cssrs4?: string;
    cssrs4Free?: string;
    cssrs5?: string;
    cssrs5Free?: string;
    readmission1?: string;
    readmission2?: string;
    fostercare?: string;
  };

  passedIntake?: boolean;
}

export interface Onboarding {
  id: string;
  created_at: Date;
  updated_at: Date;
  data: OnboardingData;
}

interface OnboardingResponse {
  results: Onboarding[];
  isLastPage: boolean;
}

export const useOnboardingResults = (
  page: number,
  perPage: number,
  search?: string,
  customFilter?: string,
  enabled = true,
  shouldKeepPreviousData = true,
  mentorMatched?: "all" | "true" | "false",
  onboardingCompleted?: "all" | "true" | "false",
) => {
  return useQuery<OnboardingResponse>({
    queryKey: [
      "onboarding",
      page,
      perPage,
      search,
      customFilter,
      mentorMatched,
      onboardingCompleted,
    ],
    queryFn: async () => {
      const response = await fetchApi<OnboardingResponse>({
        url: "/v2/onboarding",
        method: "GET",
        params: {
          page,
          limit: perPage,
          search,
          customFilter,
          mentorMatched,
          onboardingCompleted,
        },
      });

      return {
        results: response.data.results,
        isLastPage: response.data.isLastPage,
      };
    },
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
    enabled,
  });
};

// Interface for the simulation request
export interface SimulateMentorMatchRequest {
  algorithmVersion?: "v1" | "v2" | "v4";
  teen: {
    sex: string;
    // V4 Parameters
    race?: string;
    isNjGroup?: boolean;
    isAkGroup?: boolean;
    mentorPreferences?: {
      // V1 Parameters
      race?: string;
      helpWith?: string[];
      // V2 Parameters
      interests?: string[];
      topInterest?: string;
      lifeExperiences?: string[];
      personalityTraits?: string[];
      useV2Algorithm?: boolean;
    };
  };
}

export interface MentorWithSlot extends User {
  mentorSlot: {
    max_daily_slots: number;
    slots_used_today: number;
    max_total_slots: number;
    matching_status?: "open" | "auto_paused" | "manual_override" | null;
    pause_reason?: string | null;
    override?: {
      type?: string;
      direction?: string;
      endDate?: string;
      allocation?: number;
      reason?: string;
    } | null;
  };
}

// V4 simulation result (pool guarantee dry-run, no scores)
export interface SimulateMentorMatchV4Result {
  eligibleCount: number;
  poolCount: number;
  wouldAlert: boolean;
  pool: {
    mentor: MentorWithSlot;
    source: "open" | "system_override";
    sameRace: boolean;
  }[];
}

// V2 Score breakdown interface
export interface V2ScoreBreakdown {
  interestMatch: number;
  experienceMatch: number;
  personalityMatch: number;
  matchRateWeight: number;
  fairnessWeight: number;
  finalScore: number;
}

// Interface for the simulation response
export interface SimulateMentorMatchResponse {
  simulateMatchesV4?: SimulateMentorMatchV4Result;
  simulateMatches?: {
    mentor: MentorWithSlot;
    score: number;
    scoreBreakdown?: {
      // V1 breakdown
      race?: number;
      struggles?: number;
      interests?: number;
      // V2 breakdown
      v2_breakdown?: V2ScoreBreakdown;
      match_rate_data?: {
        mentorId: string;
        matchRate: number;
        hasMinimumSample: boolean;
        impressions: number;
        selections: number;
        skips: number;
      };
      weekly_impressions?: number;
    };
  }[];
}

// Hook to simulate mentor matching
export const useSimulateMentorMatch = () => {
  const { toast } = useToast();

  return useMutation<
    SimulateMentorMatchResponse,
    Error,
    SimulateMentorMatchRequest
  >({
    mutationFn: async (data) => {
      const response = await fetchApi<SimulateMentorMatchResponse>({
        url: "/v2/onboarding/simulate/matches",
        method: "POST",
        data: { data },
      });

      return response.data;
    },
    onError: (error) => {
      console.error("Failed to simulate mentor matching:", error);
      toast({
        title: "Error",
        description: "Failed to simulate mentor matching",
        variant: "destructive",
      });
    },
  });
};
